/**
 * Newsletter Popup JavaScript
 * Displays a newsletter signup popup after a specified delay
 */

class NewsletterPopup {
  constructor() {
    this.config = this.getConfig();
    this.overlay = document.getElementById('newsletter-popup-overlay');
    this.popup = document.getElementById('newsletter-popup');
    this.closeButton = document.getElementById('newsletter-popup-close');
    this.form = document.getElementById('NewsletterPopupForm');
    this.emailInput = document.getElementById('NewsletterPopupForm--email');
    this.submitButton = document.getElementById('NewsletterPopupSubmit');
    this.backgroundSubmitTimeout = null; // Track background submit timeout
    this.isSubmitting = false; // Prevent double submission
    this.hasSubmitted = false; // Track if form was already submitted
    this.inputTimeout = null; // Track input debounce timeout
    this.confettiTimeout = null; // Track confetti timeout
    this.closeTimeout = null; // Track close timeout
    this.isClosed = false; // Track if popup is already closed
    
    if (!this.overlay || !this.popup) {
      return;
    }

    window.newsletterPopup = this;
    this.init();
  }

  getConfig() {
    const configElement = document.getElementById('newsletter-popup-config');
    if (!configElement) return { enabled: false };
    
    try {
      return JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('Newsletter Popup: Error loading configuration', e);
      return { enabled: false };
    }
  }

  isMobile() {
    return window.innerWidth <= 768;
  }

  isCustomerLoggedIn() {
    // Check if customer is logged in via data attribute
    if (this.overlay) {
      const isLoggedIn = this.overlay.getAttribute('data-customer-logged-in') === 'true';
      return isLoggedIn;
    }
    return false;
  }

  hasCustomerPostedParam() {
    // Check if URL contains customer_posted=true parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('customer_posted') === 'true';
  }

  init() {
    // Global click listener for manual triggers (e.g., in footer or header)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-newsletter-popup], [href="#newsletter-popup"]');
      if (trigger) {
        e.preventDefault();
        this.show(true); // Pass true to focus email input
      }
    });

    // Event Listeners for close button & overlay
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });
    }

    // ESC key to close (only when popup is open)
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escHandler);

    // Email input handler - show button below input when user starts typing
    // Use debouncing for better performance
    if (this.emailInput && this.submitButton) {
      this.emailInput.addEventListener('input', () => {
        // Clear previous timeout
        if (this.inputTimeout) {
          clearTimeout(this.inputTimeout);
        }
        
        // Debounce the update
        this.inputTimeout = setTimeout(() => {
          if (!this.emailInput || !this.submitButton) return;
          
          const hasValue = this.emailInput.value.trim().length > 0;
          
          if (hasValue) {
            this.submitButton.style.display = 'block';
            this.submitButton.classList.add('newsletter-popup__submit-button--visible');
          } else {
            this.submitButton.style.display = 'none';
            this.submitButton.classList.remove('newsletter-popup__submit-button--visible');
          }
        }, 50); // Small debounce for smooth performance
      });
    }

    // Form Submit Handler - show success immediately, submit in background
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        // Prevent default form submission
        e.preventDefault();
        
        // Prevent double submission
        if (this.isSubmitting) {
          return;
        }
        
        // Get email value
        const emailValue = this.emailInput ? this.emailInput.value.trim() : '';
        
        // Validate email
        if (!emailValue || !this.isValidEmail(emailValue)) {
          this.showErrorMessage('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
          return;
        }
        
        // Mark as submitting
        this.isSubmitting = true;
        
        // Disable form to prevent multiple submissions
        if (this.form) {
          this.form.style.pointerEvents = 'none';
        }
        
        // Show success immediately (optimistic UI)
        this.showSuccessImmediately();
        
        // Submit form in background after 3 seconds
        this.backgroundSubmitTimeout = setTimeout(() => {
          this.submitFormInBackground();
          this.backgroundSubmitTimeout = null; // Clear timeout reference after execution
        }, 3000);
      });
    }

    // Auto-show timer (only if enabled and user has not logged in / submitted)
    if (this.config.enabled && !this.isCustomerLoggedIn() && !this.hasCustomerPostedParam()) {
      if (this.config.showOnMobile || !this.isMobile()) {
        const delay = this.isDismissed() ? 20 : (this.config.delay || 10);
        setTimeout(() => {
          this.show(false);
        }, delay * 1000);
      }
    }
  }

  isDismissed() {
    // Check sessionStorage instead of cookie
    // This only persists for the current browser session
    try {
      return sessionStorage.getItem('newsletter_popup_dismissed') === 'true';
    } catch (e) {
      // Fallback if sessionStorage is not available
      return false;
    }
  }

  setDismissed() {
    // Store in sessionStorage instead of cookie
    // This will be cleared when the browser session ends
    try {
      sessionStorage.setItem('newsletter_popup_dismissed', 'true');
    } catch (e) {
      // Silently fail if sessionStorage is not available
      console.warn('Newsletter Popup: sessionStorage not available', e);
    }
  }

  show(focusInput = false) {
    if (!this.overlay || !this.popup) return;
    
    // Reset closed flag when showing popup
    this.isClosed = false;

    // Prevent body scroll and save current scroll position
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.scrollY = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    // Show overlay
    this.overlay.style.display = 'flex';
    
    // Trigger reflow for animation
    this.overlay.offsetHeight;

    // Add animation
    this.overlay.classList.add('newsletter-popup-overlay--visible');
    this.popup.classList.add(`newsletter-popup--${this.config.animationType || 'fade'}`);

    // Focus handling: focus email input if requested, otherwise focus close button for accessibility
    if (focusInput && this.emailInput) {
      setTimeout(() => {
        if (this.emailInput) {
          this.emailInput.focus();
        }
      }, 150);
    } else if (this.closeButton) {
      this.closeButton.focus();
    }
  }

  close() {
    if (!this.overlay || !this.popup) return;
    
    // Prevent multiple close calls
    if (this.isClosed) {
      return;
    }
    
    // Mark as closed
    this.isClosed = true;

    // Mark as dismissed in sessionStorage (for 20s delay on next show)
    this.setDismissed();

    // Remove ESC handler
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }

    // Clear all pending timeouts
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
      this.inputTimeout = null;
    }
    
    if (this.confettiTimeout) {
      clearTimeout(this.confettiTimeout);
      this.confettiTimeout = null;
    }
    
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    // Clear background submit timeout if popup is closed before submission
    // Only submit if timeout is still pending (hasn't executed yet)
    if (this.backgroundSubmitTimeout) {
      clearTimeout(this.backgroundSubmitTimeout);
      this.backgroundSubmitTimeout = null;
      // Still submit in background even if popup is closed early (user already saw success)
      this.submitFormInBackground();
    }

    // Remove animation
    this.overlay.classList.remove('newsletter-popup-overlay--visible');
    this.popup.classList.remove(`newsletter-popup--${this.config.animationType || 'fade'}`);

    // Wait for animation end
    setTimeout(() => {
      // Double-check popup is still closed (prevent race conditions)
      if (!this.isClosed) return;
      
      this.overlay.style.display = 'none';
      
      // Restore body scroll only if popup was just closed
      const savedScrollY = this.scrollY;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Restore scroll position to where user was before popup opened
      if (typeof savedScrollY === 'number' && !isNaN(savedScrollY)) {
        window.scrollTo(0, savedScrollY);
        requestAnimationFrame(() => {
          window.scrollTo(0, savedScrollY);
        });
      }
      
      // Clear scroll position reference
      this.scrollY = undefined;
    }, 300);
  }

  isOpen() {
    return this.overlay && this.overlay.style.display === 'flex';
  }

  showSuccessMessage() {
    const formWrapper = this.form ? this.form.closest('.newsletter-popup__content') : null;
    if (!formWrapper) return;
    
    // Remove existing success message if any
    const existingMessage = formWrapper.querySelector('.newsletter-popup__success-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create success message
    const successMessage = document.createElement('div');
    successMessage.className = 'newsletter-popup__success-message';
    successMessage.id = 'NewsletterPopup-success';
    successMessage.style.opacity = '0';
    successMessage.style.transform = 'translateY(20px)';
    // Escape HTML to prevent XSS
    const successText = this.escapeHtml(this.getSuccessText());
    
    successMessage.innerHTML = `
      <div class="newsletter-popup__success-icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="24" fill="currentColor" opacity="0.1"/>
          <path d="M16 24l6 6 10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="newsletter-form__message newsletter-form__message--success form__message" tabindex="-1" autofocus>
        ${successText}
      </h3>
    `;
    
    // Insert after form
    if (this.form && this.form.parentNode) {
      this.form.parentNode.insertBefore(successMessage, this.form.nextSibling);
    } else {
      formWrapper.appendChild(successMessage);
    }
    
    // Trigger animation
    requestAnimationFrame(() => {
      successMessage.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out';
      successMessage.style.opacity = '1';
      successMessage.style.transform = 'translateY(0)';
    });
  }

  showErrorMessage(message) {
    const formWrapper = this.form ? this.form.closest('.newsletter-popup__content') : null;
    if (!formWrapper) return;
    
    // Remove existing error message if any
    const existingError = formWrapper.querySelector('.newsletter-popup__error-message');
    if (existingError) {
      existingError.remove();
    }
    
    // Escape HTML to prevent XSS
    const escapedMessage = this.escapeHtml(message);
    
    // Create error message
    const errorMessage = document.createElement('div');
    errorMessage.className = 'newsletter-popup__error-message newsletter-form__message form__message';
    errorMessage.innerHTML = `
      <span class="svg-wrapper">
        ${this.getErrorIcon()}
      </span>
      ${escapedMessage}
    `;
    
    // Insert after form
    if (this.form && this.form.parentNode) {
      this.form.parentNode.insertBefore(errorMessage, this.form.nextSibling);
    } else {
      formWrapper.appendChild(errorMessage);
    }
    
    // Remove error after 5 seconds
    setTimeout(() => {
      errorMessage.remove();
    }, 5000);
  }

  getSuccessText() {
    // Get from data attribute in popup content
    const popupContent = this.popup ? this.popup.querySelector('.newsletter-popup__content') : null;
    if (popupContent) {
      const successText = popupContent.getAttribute('data-success-text');
      if (successText && successText !== 'newsletter.success') {
        return successText;
      }
    }
    
    // Try to get from existing success message in DOM
    const existingSuccess = document.querySelector('.newsletter-form__message--success');
    if (existingSuccess) {
      const text = existingSuccess.textContent.trim();
      if (text) return text;
    }
    
    // Default text
    return 'Danke für deine Anmeldung';
  }

  getErrorIcon() {
    // Return error icon SVG
    return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7v-2h2v2zm0-3H7V5h2v4z" fill="currentColor"/></svg>';
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showSuccessImmediately() {
    // Hide form and button
    if (this.form) {
      this.form.style.display = 'none';
    }
    if (this.submitButton) {
      this.submitButton.style.display = 'none';
    }
    
    // Show success message immediately
    this.showSuccessMessage();
    
    // Trigger confetti animation after a short delay
    this.confettiTimeout = setTimeout(() => {
      this.triggerConfetti();
      this.confettiTimeout = null;
    }, 300);
    
    // Close popup after delay
    this.closeTimeout = setTimeout(() => {
      this.close();
      this.closeTimeout = null;
    }, 5000);
  }

  submitFormInBackground() {
    // Prevent multiple submissions
    if (this.hasSubmitted) {
      return;
    }
    
    // Validate form exists before submitting
    if (!this.form || !this.form.parentNode) {
      // Form was removed from DOM, cannot submit
      return;
    }
    
    // Validate email input still exists
    if (!this.emailInput || !this.emailInput.value) {
      // Email input missing or empty, cannot submit
      return;
    }
    
    // Mark as submitted
    this.hasSubmitted = true;
    
    // Submit form silently in background
    try {
      const formData = new FormData(this.form);
      const formAction = this.form.action || '/contact';
      
      fetch(formAction, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .catch(error => {
        // Silently handle errors - user already saw success message
        // In production, you might want to log this to analytics
        console.error('Background newsletter subscription error:', error);
      });
    } catch (error) {
      // Handle any errors during form submission preparation
      console.error('Error preparing newsletter subscription:', error);
    }
  }

  triggerConfetti() {
    // Skip confetti on low-end devices or if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Create confetti container
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'newsletter-popup__confetti';
    confettiContainer.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10001; will-change: contents;';
    
    document.body.appendChild(confettiContainer);

    // Create confetti particles - reduce count on mobile for performance
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE'];
    const isMobile = this.isMobile();
    const particleCount = isMobile ? 20 : 50; // Reduce particles on mobile

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 10 + 5;
      const startX = Math.random() * window.innerWidth;
      const startY = -10;
      const endY = window.innerHeight + 10;
      const rotation = Math.random() * 360;
      const duration = Math.random() * 2 + 2;
      const delay = Math.random() * 0.5;

      const rotationAmount = rotation + (Math.random() * 720 - 360);
      
      particle.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        left: ${startX}px;
        top: ${startY}px;
        border-radius: 50%;
        pointer-events: none;
        will-change: transform, opacity;
        transform: translateZ(0);
        --rotation: ${rotationAmount}deg;
        --end-y: ${endY}px;
        animation: confetti-fall ${duration}s ease-in ${delay}s forwards;
      `;

      confettiContainer.appendChild(particle);
    }

    // Remove confetti after animation
    setTimeout(() => {
      confettiContainer.remove();
    }, 5000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new NewsletterPopup();
  });
} else {
  new NewsletterPopup();
}
